import React from 'react';
import Lottie from 'lottie-react';
import { Box, Typography, SxProps, Theme, CircularProgress } from '@mui/material';

// Import Lottie animation data
import gearsAnimation from '../assets/animations/gears_loading.json';
import spinnerAnimation from '../assets/animations/spinner_loading.json';
import dotsAnimation from '../assets/animations/dots_loading.json';

export type LoadingVariant = 'gears' | 'spinner' | 'dots' | 'default';

interface LoadingProps {
  variant?: LoadingVariant;
  size?: number;
  message?: string;
  fullScreen?: boolean;
  sx?: SxProps<Theme>;
  color?: string;
}

const Loading: React.FC<LoadingProps> = ({
  variant = 'default',
  size = 120,
  message = 'Loading...',
  fullScreen = false,
  sx = {},
  color = '#1976d2'
}) => {
  const getAnimationData = () => {
    try {
      switch (variant) {
        case 'gears':
          return gearsAnimation;
        case 'spinner':
          return spinnerAnimation;
        case 'dots':
          return dotsAnimation;
        default:
          return gearsAnimation; // Default to gears
      }
    } catch (error) {
      console.warn('Failed to load Lottie animation, falling back to default spinner');
      return null;
    }
  };

  const animationData = getAnimationData();

  const containerSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    ...(fullScreen && {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      zIndex: 9999,
    }),
    ...sx,
  };

  const animationStyle = {
    width: size,
    height: size,
  };

  return (
    <Box sx={containerSx}>
      {animationData ? (
        <Lottie
          animationData={animationData}
          loop={true}
          autoplay={true}
          style={animationStyle}
        />
      ) : (
        <CircularProgress 
          size={size} 
          sx={{ color: color }}
        />
      )}
      {message && (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ 
            textAlign: 'center',
            color: color,
            fontWeight: 500
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default Loading;
